// src/components/challenges/ChallengeDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Shield, Clock, Award, ChevronLeft, Target, Mail, Lock, Network, Star, AlertTriangle, Play } from 'lucide-react';

const ChallengeDetail = ({ apiService }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation(); // <-- Add location hook to access state
    const [challenge, setChallenge] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lockedImageUrl, setLockedImageUrl] = useState('');

    const urlParams = new URLSearchParams(window.location.search);
    const arenaId = urlParams.get('arena');
    const topicFilter = urlParams.get('topic');
    const isDemo = urlParams.get('demo') === 'true';

    // State to store derived arena ID from challenge data
    const [derivedArenaId, setDerivedArenaId] = useState(null);

    // --- All original data and logic hooks remain unchanged ---
    // #region Original Logic (Unchanged)
    const simulationChallenges = {
        'phishing-detection': { id: 'phishing-detection', title: 'Phishing Detection Challenge', videoUrl: '/assets/videos/phishing.mp4', promoImageUrl: '/assets/images/phishing-promo.jpg', isLocked: false, unlockRequirement: '', description: 'Test your ability to identify phishing emails in a realistic email client simulation. You\'ll be presented with various emails and must correctly identify which ones are legitimate and which are phishing attempts.', category: 'social-engineering', difficulty: 'beginner', estimatedTime: '10 min', xpReward: 100, objectives: ['Identify suspicious sender addresses', 'Recognize urgency tactics', 'Spot grammar and spelling errors', 'Verify link destinations', 'Check for generic greetings'], skills: ['Email Security', 'Threat Detection', 'Critical Thinking'], icon: Mail },
        'password-security': { id: 'password-security', title: 'Password Security Assessment', videoUrl: '/assets/videos/Password_Security.mp4', promoImageUrl: '/assets/images/password-promo.jpg', isLocked: false, unlockRequirement: '', description: 'Evaluate your password security knowledge through interactive scenarios. Create strong passwords, test them against common attacks, and learn about password management best practices.', category: 'authentication', difficulty: 'beginner', estimatedTime: '8 min', xpReward: 75, objectives: ['Create strong passwords', 'Understand password entropy', 'Avoid common patterns', 'Use password managers', 'Enable two-factor authentication'], skills: ['Authentication', 'Password Management', 'Security Basics'], icon: Lock },
        'malware-identification': { id: 'malware-identification', title: 'Malware Identification Quiz', videoUrl: '/assets/videos/Software_Download.mp4', promoImageUrl: '/assets/images/malware-promo.jpg', isLocked: true, unlockRequirement: 'Unlock at 200 XP', description: 'Learn to recognize different types of malware through interactive scenarios. Analyze suspicious files, understand malware behaviors, and practice safe computing habits.', category: 'malware', difficulty: 'intermediate', estimatedTime: '15 min', xpReward: 150, objectives: ['Identify malware types', 'Recognize suspicious behaviors', 'Understand infection vectors', 'Practice safe Browse', 'Learn quarantine procedures'], skills: ['Malware Analysis', 'System Security', 'Threat Response'], icon: Shield },
        'network-security-basics': { id: 'network-security-basics', title: 'Network Security Fundamentals', videoUrl: 'https://www.youtube.com/embed/y_bnMSr4jX4', promoImageUrl: '/assets/images/network-promo.jpg', isLocked: true, unlockRequirement: 'Requires "Phishing" Badge', description: 'Explore network security concepts through hands-on simulations. Monitor network traffic, configure firewalls, and detect intrusion attempts.', category: 'network-security', difficulty: 'intermediate', estimatedTime: '25 min', xpReward: 250, objectives: ['Analyze network packets', 'Configure firewall rules', 'Detect suspicious traffic', 'Understand protocols', 'Implement security policies'], skills: ['Network Analysis', 'Firewall Configuration', 'Traffic Monitoring'], icon: Network }
    };

    // Select a random image for locked challenges on component mount
    useEffect(() => {
        const lockedImages = [
            '/assets/images/next/1.gif',
            '/assets/images/next/2.gif',
            '/assets/images/next/3.gif',
            '/assets/images/next/4.gif',
            '/assets/images/next/5.gif',
            '/assets/images/next/6.gif',
            '/assets/images/next/7.gif',
            '/assets/images/next/8.gif',
        ];
        const randomIndex = Math.floor(Math.random() * lockedImages.length);
        setLockedImageUrl(lockedImages[randomIndex]);
    }, []);

    useEffect(() => {
        const fetchChallengeData = async () => {
            setLoading(true);
            setError(null);
            try {
                if (apiService) {
                    const response = await apiService.get(`/challenges/${id}`);
                    if (response.success && response.data) {
                        const dbChallenge = response.data;
                        const transformedChallenge = {
                            id: dbChallenge.id, title: dbChallenge.title, description: dbChallenge.description, category: dbChallenge.category, difficulty: mapDifficulty(dbChallenge.difficulty), estimatedTime: dbChallenge.estimated_time || `${Math.ceil((dbChallenge.points || 50) / 10)} min`, xpReward: dbChallenge.xp_reward || dbChallenge.points || 100, videoUrl: getVideoUrl(dbChallenge.category),
                            // MODIFICATION: Prioritize the passed imageUrl over the generated one
                            promoImageUrl: location.state?.imageUrl || getPromoImage(dbChallenge.category),
                            isLocked: false, unlockRequirement: '', objectives: generateObjectives(dbChallenge.category, dbChallenge.title), skills: generateSkills(dbChallenge.category), icon: getIconForCategory(dbChallenge.category), user_progress: dbChallenge.user_progress, metadata: dbChallenge.metadata
                        };
                        setChallenge(transformedChallenge);

                        // Derive arena ID from challenge data if not present in URL
                        if (!arenaId && dbChallenge.category) {
                            // console.log('🎯 Derived arena ID from challenge data:', dbChallenge.category);
                            setDerivedArenaId(dbChallenge.category);
                        }
                    } else { throw new Error('Challenge not found in database'); }
                } else { throw new Error('API service not available'); }
            } catch (error) {
                console.error('Failed to fetch challenge from database:', error);
                const fallbackChallenge = simulationChallenges[id] || generateGenericChallenge(id);
                // MODIFICATION: Apply the passed imageUrl to fallback data as well
                if (location.state?.imageUrl) {
                    fallbackChallenge.promoImageUrl = location.state.imageUrl;
                }
                setChallenge(fallbackChallenge);
                setError(`Could not load challenge from database: ${error.message}`);

                // Derive arena ID from fallback challenge data if not present in URL
                if (!arenaId && fallbackChallenge.category) {
                    // console.log('🎯 Derived arena ID from fallback challenge data:', fallbackChallenge.category);
                    setDerivedArenaId(fallbackChallenge.category);
                }
            } finally { setLoading(false); }
        };
        fetchChallengeData();
    }, [id, apiService, location.state]);

    const mapDifficulty = (dbDifficulty) => {
        if (typeof dbDifficulty === 'number') {
            if (dbDifficulty <= 2) return 'beginner';
            if (dbDifficulty <= 4) return 'intermediate';
            return 'advanced';
        }
        return dbDifficulty || 'beginner';
    };
    const getVideoUrl = (category) => {
        const videoMap = { 'phishing': '/assets/videos/phishing.mp4', 'password_security': '/assets/videos/Password_Security.mp4', 'malware': '/assets/videos/Software_Download.mp4', 'social_engineering': '/assets/videos/phishing.mp4', 'email_security': '/assets/videos/phishing.mp4', 'web_security': '/assets/videos/Software_Download.mp4', 'network_security': '/assets/videos/Network_Security.mp4' };
        return videoMap[category] || '/assets/videos/challenge-default.mp4';
    };
    const getPromoImage = (category) => {
        const imageMap = { 'phishing': '/assets/images/phishing-promo.jpg', 'password_security': '/assets/images/password-promo.jpg', 'malware': '/assets/images/malware-promo.jpg', 'social_engineering': '/assets/images/social-promo.jpg', 'email_security': '/assets/images/email-promo.jpg' };
        return imageMap[category] || '/assets/images/next-challenge.jpg';
    };
    const generateObjectives = (category, title) => {
        const objectiveMap = { 'phishing': ['Identify suspicious sender addresses', 'Recognize urgency tactics and pressure language', 'Spot grammar and spelling inconsistencies', 'Verify link destinations before clicking', 'Check for generic greetings and impersonal language'], 'social_engineering': ['Recognize manipulation techniques', 'Identify pretexting scenarios', 'Understand authority exploitation', 'Detect information harvesting attempts', 'Apply verification protocols'], 'email_security': ['Analyze email headers for authenticity', 'Detect email spoofing attempts', 'Understand email encryption concepts', 'Implement proper email hygiene', 'Configure secure email settings'], 'password_security': ['Create strong, unique passwords', 'Understand password entropy concepts', 'Avoid common password patterns', 'Use password managers effectively', 'Enable multi-factor authentication'], 'malware': ['Identify malicious software indicators', 'Understand malware delivery methods', 'Recognize suspicious file types', 'Apply safe browsing practices', 'Implement malware prevention measures'] };
        return objectiveMap[category] || ['Complete the security challenge', 'Demonstrate understanding of key concepts'];
    };
    const generateSkills = (category) => {
        const skillMap = { 'phishing': ['Email Security', 'Threat Detection', 'Critical Thinking'], 'social_engineering': ['Social Engineering Defense', 'Human Psychology', 'Verification Protocols'], 'email_security': ['Email Security', 'Header Analysis', 'Digital Forensics'], 'password_security': ['Authentication', 'Password Management', 'Security Basics'], 'malware': ['Malware Detection', 'File Analysis', 'System Security'], 'network_security': ['Network Security', 'Traffic Analysis', 'Intrusion Detection'], 'web_security': ['Web Security', 'Safe Browsing', 'URL Analysis'] };
        return skillMap[category] || ['Security Awareness', 'Cybersecurity'];
    };
    const getIconForCategory = (category) => {
        const iconMap = { 'phishing': Mail, 'social_engineering': Target, 'email_security': Mail, 'password_security': Lock, 'malware': Shield, 'network_security': Network, 'web_security': Target };
        return iconMap[category] || Shield;
    };
    const generateGenericChallenge = (challengeId) => ({ id: challengeId, title: 'Security Challenge', description: 'A comprehensive security challenge to test your knowledge and skills.', category: 'general', difficulty: 'beginner', estimatedTime: '10 min', xpReward: 100, videoUrl: '/assets/videos/challenge-default.mp4', promoImageUrl: '/assets/images/next-challenge.jpg', isLocked: false, unlockRequirement: '', objectives: ['Complete the security assessment', 'Demonstrate understanding of security concepts'], skills: ['Security Awareness', 'Cybersecurity'], icon: Shield });

    const handleStartChallenge = () => {
        // Check if this is a simulation challenge that should use SecuritySimulationContainer
        const isSimulationChallenge =
            challenge.category === 'phishing' ||
            challenge.category === 'email_security' ||
            challenge.category === 'phishing_social_engineering' ||
            challenge.category === 'malware_ransomware' ||
            challenge.title?.toLowerCase().includes('phishing') ||
            challenge.title?.toLowerCase().includes('vishing') ||
            challenge.title?.toLowerCase().includes('phone call') ||
            challenge.title?.toLowerCase().includes('malware') ||
            challenge.title?.toLowerCase().includes('ransomware') ||
            challenge.title?.toLowerCase().includes('virus') ||
            challenge.description?.toLowerCase().includes('phone call') ||
            challenge.description?.toLowerCase().includes('vishing') ||
            challenge.description?.toLowerCase().includes('malware') ||
            challenge.description?.toLowerCase().includes('ransomware') ||
            (challenge.metadata && challenge.metadata.simulationType) ||
            challenge.hasSimulation;

        if (isSimulationChallenge) {
            // Route to SecuritySimulationContainer for simulation challenges
            navigate(`/simulation/${challenge.id}${isDemo ? '?demo=true' : ''}`);
        } else {
            // Route to ChallengeAttempt for regular challenges
            navigate(`/challenge-attempt/${challenge.id}${isDemo ? '?demo=true' : ''}`);
        }
    };
    const getDifficultyClass = (difficulty) => {
        switch (difficulty) {
            case 'beginner': return 'text-green-400 bg-green-900/50';
            case 'intermediate': return 'text-yellow-400 bg-yellow-900/50';
            case 'advanced': return 'text-red-400 bg-red-900/50';
            default: return 'text-gray-400 bg-gray-700';
        }
    };
    // #endregion

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#102c4a] to-[#164f3f] flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-400 mx-auto mb-4"></div>
                    <p className="text-gray-300">Loading challenge...</p>
                </div>
            </div>
        );
    }

    if (!challenge) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#102c4a] to-[#164f3f] flex items-center justify-center">
                <div className="text-center text-white">
                    <Shield size={48} className="mx-auto mb-4 text-gray-400" />
                    <h2 className="text-xl font-bold mb-2">Challenge Not Found</h2>
                    <p className="text-gray-400 mb-4">The requested challenge could not be loaded.</p>
                    <button
                        onClick={() => {
                            const effectiveArenaId = arenaId || derivedArenaId;
                            const backUrl = effectiveArenaId ? `/challenges?arena=${effectiveArenaId}${topicFilter ? `&topic=${topicFilter}` : ''}` : '/challenges';
                            navigate(backUrl);
                        }}
                        className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                        {(arenaId || derivedArenaId) ? 'Back to Arena' : 'Back to Challenges'}
                    </button>
                </div>
            </div>
        );
    }

    const IconComponent = challenge.icon || Shield;

    const startButtonClasses = challenge.isLocked
        ? "w-full bg-gray-700 text-gray-400 cursor-not-allowed px-8 py-4 rounded-lg font-bold text-lg flex items-center justify-center" // Locked state remains the same
        : "w-full px-8 py-4 rounded-lg border border-white/30 bg-black/20 text-white/80 backdrop-blur-sm transition-all duration-300 ease-in-out focus:outline-none hover:text-white hover:bg-cyan-900/50 hover:border-cyan-300 shadow-[0_0_15px_#0ff] font-bold text-lg flex items-center justify-center mt-20";

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#071942] to-[#3c2d0a] text-white p-3 sm:p-6 lg:p-8">
            <div className="max-w-screen-2xl mx-auto">
                <div className="grid grid-cols-1 xl:grid-cols-5 gap-6 lg:gap-8">

                    <div className="xl:col-span-1 flex flex-col gap-4 md:gap-6">
                        <button onClick={() => {
                            const effectiveArenaId = arenaId || derivedArenaId;
                            // If in demo mode, go back to demo arena
                            if (isDemo) {
                                navigate('/demo-arena');
                                return;
                            }
                            const backUrl = effectiveArenaId ? `/challenges?arena=${effectiveArenaId}${topicFilter ? `&topic=${topicFilter}` : ''}` : '/dashboard';
                            navigate(backUrl);
                        }}
                            className="flex items-center text-gray-300 hover:text-white transition-all duration-300 p-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg justify-center lg:justify-start hover:shadow-[0_0_15px_#0ff] hover:border-cyan-400">
                            <ChevronLeft size={20} className="mr-2" />
                            {isDemo ? 'Back to Demo Arena' : (arenaId || derivedArenaId) ? 'Back to Arena' : 'Back to Dashboard'}
                        </button>

                        {isDemo && (
                            <div className="bg-blue-900/30 border border-blue-500/50 rounded-lg p-4">
                                <div className="flex items-center text-blue-400 text-sm font-bold">
                                    <Play size={16} className="mr-2" />
                                    DEMO MODE
                                </div>
                                <p className="text-xs text-blue-300 mt-1">
                                    Progress and points will not be saved.
                                </p>
                            </div>
                        )}

                        {error && (
                            <div className="bg-yellow-900/30 border border-yellow-600/50 rounded-lg p-4">
                                <div className="flex items-center text-yellow-400 text-sm">
                                    <AlertTriangle size={16} className="mr-2" />
                                    Using fallback data - database unavailable
                                </div>
                            </div>
                        )}

                        <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-4 md:p-6 flex-grow">
                            <h2 className="text-xl md:text-2xl font-bold mb-4 text-blue-400">Overview</h2>
                            <p className="text-gray-300 leading-relaxed">{challenge.description}</p>
                        </div>
                    </div>

                    <div className="xl:col-span-2 flex flex-col gap-4 md:gap-6 mt-0 xl:mt-0">
                        <div className="bg-black rounded-xl overflow-hidden border border-[rgba(255,255,255,0.1)] shadow-2xl h-64 sm:h-80 lg:h-[420px]">
                            <img src={challenge.isLocked ? lockedImageUrl : challenge.promoImageUrl} alt={challenge.title} className="w-full h-full object-cover" />
                        </div>
                        <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-4 md:p-6 flex-grow">
                            <h3 className="text-lg md:text-xl font-bold mb-4 flex items-center text-blue-400"><Target size={22} className="mr-3" /> Learning Objectives</h3>
                            <ul className="space-y-3">{challenge.objectives.map((objective, index) => <li key={index} className="flex items-start"><span className="text-blue-400 mr-3 mt-1">▶</span> <span className="text-gray-300">{objective}</span></li>)}</ul>
                        </div>
                    </div>

                    <div className="xl:col-span-1 mt-0 xl:mt-0 flex">
                        <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-4 md:p-6 xl:sticky xl:top-6 shadow-2xl w-full">
                            <div className="flex items-start mb-4">
                                <div><h1 className="text-2xl md:text-3xl font-bold leading-tight">{challenge.title}</h1></div>
                            </div>
                            <div className="border-t border-[rgba(255,255,255,0.1)] my-6"></div>
                            <div className="space-y-4 mb-6">
                                <div className="flex items-center justify-between"><p className="text-gray-400 flex items-center"><Star size={16} className="mr-2" /> Difficulty</p><p className={`font-semibold capitalize px-3 py-1 text-sm rounded-full ${getDifficultyClass(challenge.difficulty)}`}>{challenge.difficulty}</p></div>
                                <div className="flex items-center justify-between"><p className="text-gray-400 flex items-center"><Clock size={16} className="mr-2" /> Est. Time (sec)</p><p className="font-semibold">{challenge.estimatedTime}</p></div>
                                <div className="flex items-center justify-between"><p className="text-gray-400 flex items-center"><Award size={16} className="mr-2" /> XP Reward</p><p className="font-semibold text-purple-400">{challenge.xpReward} XP</p></div>
                            </div>
                            <div className="border-t border-[rgba(255,255,255,0.1)] my-6"></div>
                            <div>
                                <h3 className="text-lg font-semibold mb-3 flex items-center"><Shield size={20} className="mr-2 text-purple-400" /> Skills You'll Practice</h3>
                                <div className="flex flex-wrap gap-2">{challenge.skills.map((skill, index) => <span key={index} className="px-3 py-1 bg-gray-700/80 hover:bg-gray-700 transition-colors rounded-full text-sm">{skill}</span>)}</div>
                            </div>
                            <div className="border-t border-[rgba(255,255,255,0.1)] my-6"></div>
                            <button onClick={handleStartChallenge} disabled={challenge.isLocked} className={startButtonClasses}>
                                {challenge.isLocked ? <><Lock size={22} className="mr-2" /> <span>Challenge Locked</span></> : <><Target size={22} className="mr-2" /> <span>Attempt Challenge</span></>}
                            </button>
                        </div>
                    </div>

                    <div className="relative xl:col-span-1 mt-0 xl:mt-0 h-48 sm:h-64 xl:h-full">
                        <img src={lockedImageUrl} alt="Challenge visual" className="w-full h-full object-cover rounded-2xl shadow-lg border border-[rgba(255,255,255,0.1)]" />

                        {challenge.isLocked && (
                            <div className="absolute inset-0 bg-black/70 rounded-2xl flex items-center justify-center p-4 backdrop-blur-sm">
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
};

export default ChallengeDetail;